import MarkdownIt from 'markdown-it';
import TurndownService from 'turndown';
import { sanitizeNoteHtml } from './sanitizeHtml';
import { isPrefixCiphertext } from './encryptionPolicy';

const markdown = new MarkdownIt();
const turndown = new TurndownService({
  bulletListMarker: '-',
  codeBlockStyle: 'fenced',
  emDelimiter: '_',
  headingStyle: 'atx',
  strongDelimiter: '**'
});
const HEADER_PREFIX = '<!-- eznotemanager-export:v2 token=';
const V3_HEADER_PREFIX = '<!-- eznotemanager-export:v3 token=';
const START_PREFIX = '<!-- eznotemanager-note:start token=';
const END_PREFIX = '<!-- eznotemanager-note:end token=';
const META_PREFIX = '<!-- eznotemanager-note:meta ';

const createExportToken = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `ezn-${crypto.randomUUID()}`;
  }

  return `ezn-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
};

const escapeCommentJson = (metadata) => JSON.stringify(metadata).replace(/-->/g, '--\\u003e');

const presentationText = (value) => String(value).replace(/[\r\n]+/g, ' ');

const normalizeMetadata = (metadata) => {
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) {
    throw new Error('Malformed EzNoteManager v2 metadata.');
  }

  const isEncrypted = metadata.contentType === 'encrypted';
  if (typeof metadata.title !== 'string' ||
      (metadata.category !== undefined && typeof metadata.category !== 'string') ||
      (metadata.tags !== undefined && (!Array.isArray(metadata.tags) || metadata.tags.some(tag => typeof tag !== 'string'))) ||
      (metadata.contentType !== undefined && !isEncrypted) ||
      (isEncrypted && (typeof metadata.content !== 'string' || !metadata.content.startsWith('encrypted:')))) {
    throw new Error('Malformed EzNoteManager v2 metadata.');
  }

  const normalized = {
    title: metadata.title || 'Untitled Note',
    category: metadata.category || '',
    tags: metadata.tags || []
  };
  if (isEncrypted) {
    normalized.contentType = 'encrypted';
    normalized.content = metadata.content;
  }
  return normalized;
};

export const markdownToSanitizedHtml = (content) => sanitizeNoteHtml(markdown.render(content || ''));

export const noteHtmlToMarkdown = (html) => turndown.turndown(sanitizeNoteHtml(html || ''));

export const serializeNotesToMarkdownV2 = (notes, toMarkdown, token = createExportToken(), encryptionPolicy) => {
  const normalizedToken = String(token);
  if (!/^[A-Za-z0-9_-]+$/.test(normalizedToken)) {
    throw new Error('Invalid EzNoteManager export token.');
  }

  const output = [`${HEADER_PREFIX}${normalizedToken} -->`];
  notes.forEach(note => {
    if (note.protection === 'vault') {
      throw new Error('Vault notes require EzNoteManager v3 export.');
    }
    const metadata = {
      title: note.title || 'Untitled Note',
      category: note.category || '',
      tags: Array.isArray(note.tags) ? note.tags : []
    };
    const isEncrypted = isPrefixCiphertext(note.content, encryptionPolicy);
    if (isEncrypted) {
      metadata.contentType = 'encrypted';
      metadata.content = note.content;
    }
    output.push(`${START_PREFIX}${normalizedToken} -->`);
    output.push(`${META_PREFIX}${escapeCommentJson(metadata)} -->`);
    output.push(`## ${presentationText(metadata.title)}`);
    if (metadata.category) output.push(`Category: ${presentationText(metadata.category)}`);
    if (metadata.tags.length > 0) output.push(`Tags: ${metadata.tags.map(presentationText).join(', ')}`);
    if (!isEncrypted) output.push(toMarkdown(note.content || '').trim());
    output.push(`${END_PREFIX}${normalizedToken} -->`);
  });

  return `${output.join('\n\n')}\n`;
};

const readCommentToken = (content, index, prefix) => {
  if (!content.startsWith(prefix, index)) {
    throw new Error('Malformed EzNoteManager v2 note marker.');
  }

  const end = content.indexOf(' -->', index + prefix.length);
  if (end === -1) throw new Error('Malformed EzNoteManager v2 note marker.');
  return { token: content.slice(index + prefix.length, end), nextIndex: end + 4 };
};

export const isV2MarkdownExport = (content) => {
  if (typeof content !== 'string') return false;
  const start = content.charCodeAt(0) === 0xFEFF ? 1 : 0;
  return content.startsWith(HEADER_PREFIX, start);
};

export const parseMarkdownV2 = (content, encryptionPolicy) => {
  const startIndex = typeof content === 'string' && content.charCodeAt(0) === 0xFEFF ? 1 : 0;
  if (!isV2MarkdownExport(content)) {
    throw new Error('Malformed EzNoteManager v2 export header.');
  }

  const header = readCommentToken(content, startIndex, HEADER_PREFIX);
  if (!/^[A-Za-z0-9_-]+$/.test(header.token)) {
    throw new Error('Malformed EzNoteManager v2 export token.');
  }

  const notes = [];
  let index = header.nextIndex;
  while (index < content.length) {
    const whitespace = content.slice(index).match(/^\s*/)[0];
    index += whitespace.length;
    if (index === content.length) break;

    const start = readCommentToken(content, index, START_PREFIX);
    if (start.token !== header.token) {
      throw new Error('Mismatched EzNoteManager v2 note token.');
    }
    index = start.nextIndex;
    index += content.slice(index).match(/^\r?\n+/)[0].length;

    if (!content.startsWith(META_PREFIX, index)) {
      throw new Error('Malformed EzNoteManager v2 metadata marker.');
    }
    const metadataEnd = content.indexOf(' -->', index + META_PREFIX.length);
    if (metadataEnd === -1) throw new Error('Malformed EzNoteManager v2 metadata marker.');
    let metadata;
    try {
      metadata = normalizeMetadata(JSON.parse(content.slice(index + META_PREFIX.length, metadataEnd)));
    } catch (error) {
      throw new Error('Malformed EzNoteManager v2 metadata.');
    }
    index = metadataEnd + 4;
    index += content.slice(index).match(/^\r?\n+/)[0].length;

    if (!content.startsWith('## ', index)) {
      throw new Error('Malformed EzNoteManager v2 presentation title.');
    }
    const titleLineEnd = content.indexOf('\n', index);
    if (titleLineEnd === -1) throw new Error('Malformed EzNoteManager v2 presentation title.');
    index = titleLineEnd + 1;
    index += content.slice(index).match(/^\r?\n+/)[0].length;
    while (content.startsWith('Category: ', index) || content.startsWith('Tags: ', index)) {
      const metadataLineEnd = content.indexOf('\n', index);
      if (metadataLineEnd === -1) throw new Error('Malformed EzNoteManager v2 presentation metadata.');
      index = metadataLineEnd + 1;
      index += content.slice(index).match(/^\r?\n+/)[0].length;
    }

    const nextEnd = content.indexOf(END_PREFIX, index);
    if (nextEnd === -1) throw new Error('Missing EzNoteManager v2 note end marker.');
    const body = content.slice(index, nextEnd).replace(/\s+$/, '');
    const end = readCommentToken(content, nextEnd, END_PREFIX);
    if (end.token !== header.token) {
      throw new Error('Mismatched EzNoteManager v2 note token.');
    }
    index = end.nextIndex;

    const { contentType, content: encryptedContent, ...noteMetadata } = metadata;
    const isLegacyEncryptedContent = contentType !== 'encrypted' && isPrefixCiphertext(body, encryptionPolicy);
    const isEncryptedContent = contentType === 'encrypted' || isLegacyEncryptedContent;
    notes.push({
      ...noteMetadata,
      content: contentType === 'encrypted' ? encryptedContent : isEncryptedContent ? body : markdownToSanitizedHtml(body),
      isEncryptedContent,
      isLegacyEncryptedContent,
      createdAt: new Date(),
      updatedAt: new Date()
    });
  }

  return notes;
};

export const parseLegacyMarkdown = (content) => {
  // Legacy exports use H2 headings as separators, so internal H2 recovery remains best-effort.
  const sections = content.split(/^##\s+/m).filter(Boolean);

  return sections.map(section => {
    const lines = section.trim().split('\n');
    const title = lines.shift().trim();
    let category = '';
    let tags = [];
    const body = lines.filter(line => {
      const categoryMatch = line.match(/^\*\*Category:\*\*\s*(.*)$/i) || line.match(/^Category:\s*(.*)$/i);
      if (categoryMatch) {
        category = categoryMatch[1].trim();
        return false;
      }
      const tagsMatch = line.match(/^\*\*Tags:\*\*\s*(.*)$/i) || line.match(/^Tags:\s*(.*)$/i);
      if (tagsMatch) {
        tags = tagsMatch[1].split(',').map(tag => tag.trim()).filter(Boolean);
        return false;
      }
      return true;
    }).join('\n').trim();

    return title && body ? {
      title,
      content: markdownToSanitizedHtml(body),
      category,
      tags,
      createdAt: new Date(),
      updatedAt: new Date()
    } : null;
  }).filter(Boolean);
};

const normalizeV3Metadata = (metadata) => {
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata) ||
    !Object.keys(metadata).every((key) => ['title', 'category', 'tags', 'protection', 'contentType', 'content'].includes(key)) ||
    typeof metadata.title !== 'string' || (metadata.category !== undefined && typeof metadata.category !== 'string') ||
    (metadata.tags !== undefined && (!Array.isArray(metadata.tags) || metadata.tags.some((tag) => typeof tag !== 'string'))) ||
    !['standard', 'vault'].includes(metadata.protection)) throw new Error('Malformed EzNoteManager v3 metadata.');
  if (metadata.protection === 'vault') {
    if (metadata.contentType !== 'encrypted' || typeof metadata.content !== 'string' || !/^encrypted:.+/.test(metadata.content)) throw new Error('Malformed EzNoteManager v3 metadata.');
  } else if (metadata.contentType !== 'markdown' || Object.prototype.hasOwnProperty.call(metadata, 'content')) {
    throw new Error('Malformed EzNoteManager v3 metadata.');
  }
  return { title: metadata.title || 'Untitled Note', category: metadata.category || '', tags: metadata.tags || [], protection: metadata.protection, contentType: metadata.contentType, content: metadata.content };
};

export const serializeNotesToMarkdownV3 = (notes, toMarkdown, token = createExportToken()) => {
  const normalizedToken = String(token);
  if (!/^[A-Za-z0-9_-]+$/.test(normalizedToken)) throw new Error('Invalid EzNoteManager export token.');
  const output = [`${V3_HEADER_PREFIX}${normalizedToken} -->`];
  notes.forEach((note) => {
    const protection = note.protection || 'standard';
    if (!['standard', 'vault'].includes(protection) || typeof note.content !== 'string' ||
      (protection === 'vault' && !/^encrypted:.+/.test(note.content)) ||
      (protection === 'standard' && note.content.startsWith('encrypted:'))) throw new Error('Invalid EzNoteManager v3 note.');
    const metadata = { title: note.title || 'Untitled Note', category: note.category || '', tags: Array.isArray(note.tags) ? note.tags : [], protection,
      contentType: protection === 'vault' ? 'encrypted' : 'markdown' };
    if (protection === 'vault') metadata.content = note.content;
    output.push(`${START_PREFIX}${normalizedToken} -->`, `${META_PREFIX}${escapeCommentJson(metadata)} -->`, `## ${presentationText(metadata.title)}`);
    if (metadata.category) output.push(`Category: ${presentationText(metadata.category)}`);
    if (metadata.tags.length) output.push(`Tags: ${metadata.tags.map(presentationText).join(', ')}`);
    if (protection === 'standard') output.push(toMarkdown(note.content).trim());
    output.push(`${END_PREFIX}${normalizedToken} -->`);
  });
  return `${output.join('\n\n')}\n`;
};

export const isV3MarkdownExport = (content) => typeof content === 'string' && content.startsWith(V3_HEADER_PREFIX, content.charCodeAt(0) === 0xFEFF ? 1 : 0);

export const parseMarkdownV3 = (content) => {
  const startIndex = typeof content === 'string' && content.charCodeAt(0) === 0xFEFF ? 1 : 0;
  if (!isV3MarkdownExport(content)) throw new Error('Malformed EzNoteManager v3 export header.');
  const header = readCommentToken(content, startIndex, V3_HEADER_PREFIX);
  if (!/^[A-Za-z0-9_-]+$/.test(header.token)) throw new Error('Malformed EzNoteManager v3 export token.');
  const notes = []; let index = header.nextIndex;
  while (index < content.length) {
    index += content.slice(index).match(/^\s*/)[0].length;
    if (index === content.length) break;
    const start = readCommentToken(content, index, START_PREFIX);
    if (start.token !== header.token) throw new Error('Mismatched EzNoteManager v3 note token.');
    index = start.nextIndex + content.slice(start.nextIndex).match(/^\r?\n+/)[0].length;
    if (!content.startsWith(META_PREFIX, index)) throw new Error('Malformed EzNoteManager v3 metadata marker.');
    const metadataEnd = content.indexOf(' -->', index + META_PREFIX.length);
    if (metadataEnd === -1) throw new Error('Malformed EzNoteManager v3 metadata marker.');
    let metadata;
    try { metadata = normalizeV3Metadata(JSON.parse(content.slice(index + META_PREFIX.length, metadataEnd))); } catch (_) { throw new Error('Malformed EzNoteManager v3 metadata.'); }
    index = metadataEnd + 4 + content.slice(metadataEnd + 4).match(/^\r?\n+/)[0].length;
    if (!content.startsWith('## ', index)) throw new Error('Malformed EzNoteManager v3 presentation title.');
    const titleEnd = content.indexOf('\n', index);
    if (titleEnd === -1) throw new Error('Malformed EzNoteManager v3 presentation title.');
    index = titleEnd + 1 + content.slice(titleEnd + 1).match(/^\r?\n+/)[0].length;
    while (content.startsWith('Category: ', index) || content.startsWith('Tags: ', index)) {
      const lineEnd = content.indexOf('\n', index); if (lineEnd === -1) throw new Error('Malformed EzNoteManager v3 presentation metadata.');
      index = lineEnd + 1 + content.slice(lineEnd + 1).match(/^\r?\n+/)[0].length;
    }
    const nextEnd = content.indexOf(END_PREFIX, index);
    if (nextEnd === -1) throw new Error('Missing EzNoteManager v3 note end marker.');
    const body = content.slice(index, nextEnd).replace(/\s+$/, '');
    const end = readCommentToken(content, nextEnd, END_PREFIX);
    if (end.token !== header.token) throw new Error('Mismatched EzNoteManager v3 note token.');
    if (metadata.protection === 'vault' && body) throw new Error('Malformed EzNoteManager v3 Vault body.');
    notes.push({ title: metadata.title, category: metadata.category, tags: metadata.tags, protection: metadata.protection,
      content: metadata.protection === 'vault' ? metadata.content : markdownToSanitizedHtml(body), isEncryptedContent: metadata.protection === 'vault', createdAt: new Date(), updatedAt: new Date() });
    index = end.nextIndex;
  }
  return notes;
};
